import { z } from 'zod';
import { Confidence, Id, IsoDate, Money, PlaceCode, PriceLevel } from './common';

export const PlaceRef = z.object({
  code: PlaceCode,
  city: z.string().trim().min(1).max(80),
  /** Linha de apoio do campo, ex.: "SAO · GRU, CGH e VCP". */
  detail: z.string().trim().max(120).optional(),
});
export type PlaceRef = z.infer<typeof PlaceRef>;

const RouteFields = z.object({
  tripId: Id.nullable().optional(),
  origin: PlaceRef,
  destination: PlaceRef,
  outboundDate: IsoDate,
  /** null = só ida. */
  returnDate: IsoDate.nullable().optional(),
  adults: z.number().int().min(1).max(9).default(1),
  directOnly: z.boolean().default(false),
  groupAirports: z.boolean().default(true),
  flexDays: z.boolean().default(false),
  targetPrice: Money.min(1).nullable().optional(),
  alertEnabled: z.boolean().default(true),
});

export const RouteInput = RouteFields.refine((v) => v.origin.code !== v.destination.code, {
  message: 'Origem e destino precisam ser diferentes.',
  path: ['destination'],
}).refine((v) => !v.returnDate || v.returnDate >= v.outboundDate, {
  message: 'A volta precisa ser depois da ida.',
  path: ['returnDate'],
});
export type RouteInput = z.input<typeof RouteInput>;

/** Edição de rota: só o que não muda a busca (mudar trecho ou data = nova rota). */
export const RouteUpdate = z.object({
  tripId: Id.nullable().optional(),
  targetPrice: Money.min(1).nullable().optional(),
  alertEnabled: z.boolean().optional(),
  status: z.enum(['active', 'paused']).optional(),
  /** Liga ou desliga a comparação de datas vizinhas (WP-03). */
  flexDays: z.boolean().optional(),
});
export type RouteUpdate = z.infer<typeof RouteUpdate>;

export const RouteStatus = z.enum(['active', 'paused', 'expired']);
export type RouteStatus = z.infer<typeof RouteStatus>;

export const LatestPrice = z.object({
  price: Money,
  level: PriceLevel,
  typicalLow: Money,
  typicalHigh: Money,
  /** Variação percentual inteira desde a checagem anterior (negativa = caiu). */
  deltaPct: z.number().int().nullable(),
  checkedAt: z.string(),
});
export type LatestPrice = z.infer<typeof LatestPrice>;

export const Chance = z.object({
  /** 0–100. */
  chance: z.number().int().min(0).max(100),
  confidence: Confidence,
  horizonDays: z.number().int().positive(),
  basis: z.string(),
});
export type Chance = z.infer<typeof Chance>;

export const RouteSummary = z.object({
  id: z.string(),
  tripId: z.string().nullable(),
  origin: PlaceRef,
  destination: PlaceRef,
  outboundDate: z.string(),
  returnDate: z.string().nullable(),
  adults: z.number().int(),
  directOnly: z.boolean(),
  groupAirports: z.boolean(),
  flexDays: z.boolean(),
  targetPrice: Money.nullable(),
  alertEnabled: z.boolean(),
  status: RouteStatus,
  latest: LatestPrice.nullable(),
  /** Preços do mais antigo para hoje, para o minigráfico do card (até 14). */
  trend: z.array(Money),
  chance: Chance.nullable(),
  createdAt: z.string(),
});
export type RouteSummary = z.infer<typeof RouteSummary>;

export const PricePoint = z.object({ day: z.string(), price: Money });
export type PricePoint = z.infer<typeof PricePoint>;

export const RouteStats = z.object({
  min: Money,
  max: Money,
  avg: Money,
  typicalLow: Money,
  typicalHigh: Money,
  days: z.number().int(),
});
export type RouteStats = z.infer<typeof RouteStats>;

/** Alvos sugeridos (WP-05): do mais seguro ao mais ousado, todos com a chance no mesmo horizonte. */
export const SuggestedTargets = z.object({
  horizonDays: z.number().int().positive(),
  confidence: Confidence,
  items: z.array(
    z.object({
      kind: z.enum(['provavel', 'bom_negocio', 'ousado']),
      target: Money,
      chance: z.number().int().min(0).max(100),
    }),
  ),
});
export type SuggestedTargets = z.infer<typeof SuggestedTargets>;

export const RouteDetail = RouteSummary.extend({
  history: z.array(PricePoint),
  stats: RouteStats.nullable(),
  /** Link para ver as passagens na fonte (Google Flights). */
  bookingUrl: z.string().url().nullable(),
  lastError: z.string().nullable(),
  /** Calculados na checagem; ausentes enquanto falta histórico. */
  suggestedTargets: SuggestedTargets.nullable().optional(),
  /** Compre agora ou espere (WP-06): leitura do histórico, nunca garantia. */
  advice: z
    .object({
      verdict: z.enum(['comprar', 'esperar', 'acompanhar']),
      headline: z.string(),
      reasons: z.array(z.string()),
    })
    .nullable()
    .optional(),
  /** Datas vizinhas ± 3 dias (WP-03), da última comparação. bestOffset só quando economiza de verdade. */
  flex: z
    .object({
      checkedAt: z.string(),
      basePrice: Money,
      bestOffset: z.number().int().nullable(),
      options: z.array(
        z.object({
          offset: z.number().int(),
          outboundDate: z.string(),
          returnDate: z.string().nullable(),
          price: Money,
          deltaPct: z.number().int(),
        }),
      ),
    })
    .nullable()
    .optional(),
  /** Todo o histórico guardado da rota (WP-13); o gráfico mostra no máximo 90 dias. */
  allTimeStats: z
    .object({ min: Money, max: Money, avg: Money, days: z.number().int(), since: z.string() })
    .nullable()
    .optional(),
});
export type RouteDetail = z.infer<typeof RouteDetail>;

export const RouteList = z.object({ routes: z.array(RouteSummary) });
export type RouteList = z.infer<typeof RouteList>;
